import React from "react";
import AppModal from "../AppModal/AppModal";
import { PostApiMethod } from "../../core/services/apiService";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from "react-redux";

interface VoiceCallCustomerDetailsProps {
    isOpen: boolean;
    onClose: () => void;
    callSid?: string;
    existingCustomerDetails?: any[];
    locationId?: string;
}

export interface VoiceCallCustomerDetailsRef {
    autoSubmit: () => Promise<void>;
}

// Utility Functions

const removeCountryCode = (phoneNumber: string): string => {
    const digitsOnly = phoneNumber?.replace(/\D/g, '');

    // If number starts with 1 and has 11 digits, remove the leading 1
    if (digitsOnly?.length === 11 && digitsOnly?.startsWith('1')) {
        return digitsOnly.substring(1);
    }

    return digitsOnly;
};

/**
 * Validates phone number format
 * Accepts: digits, spaces, hyphens, parentheses, and plus sign
 * Minimum 10 digits required
 */
const validatePhoneNumber = (phoneNumber: string): { isValid: boolean; error: string } => {
    if (!phoneNumber.trim()) {
        return { isValid: false, error: "Phone number is required" };
    }

    // Remove all non-digit characters to count digits
    const digitsOnly = phoneNumber?.replace(/\D/g, '');

    if (digitsOnly?.length < 10) {
        return { isValid: false, error: "Phone number must contain at least 10 digits" };
    }

    if (digitsOnly?.length > 15) {
        return { isValid: false, error: "Phone number cannot exceed 15 digits" };
    }

    // Check for valid characters only (digits, spaces, hyphens, parentheses, plus)
    const validFormat = /^[\d\s\-()+ ]+$/.test(phoneNumber);
    if (!validFormat) {
        return { isValid: false, error: "Phone number contains invalid characters" };
    }

    return { isValid: true, error: "" };
};

/**
 * Validates new customer form data
 */
const validateNewCustomer = (
    tenantDetails: any,
    dateNeeded: Date | null,
    isAutoSubmit: boolean
): { [key: string]: string } => {
    const newErrors: { [key: string]: string } = {};

    if (!isAutoSubmit) {
        if (!tenantDetails.firstName.trim()) newErrors.firstName = "First Name is required";
        if (!tenantDetails.lastName.trim()) newErrors.lastName = "Last Name is required";
        if (!dateNeeded) newErrors.dateNeeded = "Date Needed is required";
        if (!tenantDetails.advSource) newErrors.advSource = "Advertising Source is required";
    }

    return newErrors;
};

/**
 * Determines classification defaults for existing customers
 * Uses priority: Inquiry > Reservation > Rental
 */
const determineClassificationDefaults = (
    classification: string,
    customer: any | undefined
) => {
    let finalClassification = classification;
    let finalInquiryNo = "";
    let finalReservationNumber = "";
    let finalLeaseNumber = "";

    // If no classification selected, use priority: Inquiry > Reservation > Rental
    if (!finalClassification) {
        if (customer?.Inquiries?.length > 0) {
            finalClassification = "Inquiry";
        } else if (customer?.Reservations?.length > 0) {
            finalClassification = "Reservation";
        } else if (customer?.Rentals?.length > 0) {
            finalClassification = "Rental";
        }
    }

    // Apply default first record logic for each classification
    if (finalClassification === "Inquiry") {
        if (customer?.Inquiries?.length > 0) {
            finalInquiryNo = customer.Inquiries[0].InquiryNumber;
        }
    } else if (finalClassification === "Reservation") {
        if (customer?.Reservations?.length > 0) {
            finalReservationNumber = customer.Reservations[0].ReservationNumber;
        }
    } else if (finalClassification === "Rental") {
        if (customer?.Rentals?.length > 0) {
            finalLeaseNumber = customer.Rentals[0].LeaseNumber;
        }
    }

    return { finalClassification, finalInquiryNo, finalReservationNumber, finalLeaseNumber };
};

/**
 * Builds payload for new customer submission
 */
const buildNewCustomerPayload = (
    tenantDetails: any,
    dateNeeded: Date | null,
    callSid: string | undefined,
    locationId: string | undefined,
    facilityId: any,
    agentName: string,
    customerFirstName: string,
    customerLastName: string
) => {
    return {
        tenantId: tenantDetails.tenantId.trim() || 0,
        firstName: tenantDetails.firstName.trim(),
        lastName: tenantDetails.lastName.trim(),
        facilityId: facilityId,
        countryId: tenantDetails.countryId || 1,
        dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : tenantDetails.dateNeeded,
        advSource: tenantDetails.advSource || "Voice Call",
        customerType: "new",
        callType: "voice",
        callSid: callSid,
        locationId: locationId,
        agentName: agentName
    };
};

/**
 * Builds payload for existing customer submission
 */
const buildExistingCustomerPayload = (
    tenantDetails: any,
    customer: any | undefined,
    callSid: string | undefined,
    locationId: string | undefined,
    facilityId: any,
    classificationDefaults: {
        finalClassification: string;
        finalInquiryNo: string;
        finalReservationNumber: string;
        finalLeaseNumber: string;
    },
    agentName: string,
    customerFirstName: string,
    customerLastName: string
) => {
    const { finalClassification, finalInquiryNo, finalReservationNumber, finalLeaseNumber } = classificationDefaults;

    // Common fields for all classifications
    const basePayload = {
        classification: finalClassification,
        customerType: "existing",
        callType: "voice",
        locationId: locationId,
        callSid: callSid,
        facilityId: facilityId,
        advSource: "Voice Call",
        agentName: agentName,
        customerFirstName: customerFirstName,
        customerLastName: customerLastName
    };

    // Build payload based on final classification
    if (finalClassification === "Inquiry") {
        return {
            facilityId: facilityId,
            InquiryNo: tenantDetails.inquiryDetails || finalInquiryNo,
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    } else if (finalClassification === "Reservation") {
        return {
            facilityId: facilityId,
            ReservationNumber: tenantDetails.reservationDetails || finalReservationNumber,
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    } else if (finalClassification === "Rental") {
        return {
            TenantId: customer?.TenantID?.toString() || "",
            LeaseNumber: tenantDetails.rentalDetails || finalLeaseNumber,
            facilityId: facilityId,
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    } else {
        return {
            classification: finalClassification,
            customerType: "existing",
            callType: "voice",
            locationId: locationId,
            callSid: callSid,
            facilityId: facilityId,
            agentName: agentName,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName
        };
    }
};

/**
 * Submits customer details to API
 */
const submitCustomerDetails = async (payload: any): Promise<{ success: boolean; error?: string }> => {
    try {
        console.log(payload, 'Customer Payload (voice) ==============>');
        const res = await PostApiMethod("tenant/sendCallSummary/postCustomerDetails", payload);
        console.log("Customer API Response:", res);
        return { success: true };
    } catch (err) {
        console.error("Customer API Error:", err);
        return {
            success: false,
            error: err instanceof Error ? err.message : "Failed to submit customer details. Please try again."
        };
    }
};

// Empty State Component
interface EmptyStateProps {
    type: 'inquiry' | 'reservation' | 'rental';
    onSwitchToNew?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ type, onSwitchToNew }) => {
    const emptyStateConfig = {
        inquiry: {
            icon: 'bi-inbox',
            title: 'No Inquiry Records',
            message: 'This customer doesn\'t have any inquiry records.',
        },
        reservation: {
            icon: 'bi-calendar-x',
            title: 'No Reservation Records',
            message: 'This customer doesn\'t have any active reservations.',
        },
        rental: {
            icon: 'bi-house-x',
            title: 'No Rental Records',
            message: 'This customer doesn\'t have any active rental agreements.',
        }
    };

    const config = emptyStateConfig[type];

    return (
        <div className="text-center py-3">
            <div className="mb-2">
                <i className={`bi ${config.icon} text-muted`} style={{ fontSize: '2.5rem', opacity: 0.4 }}></i>
            </div>
            <h6 className="text-muted mb-1" style={{ fontSize: '0.95rem' }}>{config.title}</h6>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                {config.message}
            </p>
        </div>
    );
};

// Reusable Record Card Component
interface RecordCardProps {
    record: any;
    type: 'inquiry' | 'reservation' | 'rental';
    isSelected: boolean;
    onSelect: () => void;
}

const RecordCard: React.FC<RecordCardProps> = ({ record, type, isSelected, onSelect }) => {
    const cardConfig = {
        inquiry: {
            icon: 'bi-question-circle-fill',
            borderColor: 'border-info',
            bgColor: 'bg-info',
            badgeBg: isSelected ? 'bg-info text-white' : 'bg-secondary',
            checkBg: 'bg-info',
            label: 'Inquiry',
            number: record.InquiryNumber || 'Inquiry',
        },
        reservation: {
            icon: 'bi-calendar-check-fill',
            borderColor: 'border-warning',
            bgColor: 'bg-warning',
            badgeBg: isSelected ? 'bg-warning text-dark' : 'bg-secondary',
            checkBg: 'bg-warning',
            label: 'Reservation',
            number: record.ReservationNumber,
        },
        rental: {
            icon: 'bi-house-fill',
            borderColor: 'border-success',
            bgColor: 'bg-success',
            badgeBg: isSelected ? 'bg-success text-white' : 'bg-secondary',
            checkBg: 'bg-success',
            label: 'Rental',
            number: record.LeaseNumber || `REN`,
        }
    };

    const config = cardConfig[type];

    return (
        <div className="col-md-6 col-lg-3">
            <div
                className={`card h-100 ${isSelected ? `border-2 ${config.borderColor} ${config.bgColor} bg-opacity-15 shadow` : 'border border-secondary'}`}
                style={{
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                }}
                onClick={onSelect}
                onMouseEnter={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isSelected) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                    }
                }}
            >
                {isSelected && (
                    <div className="position-absolute top-0 end-0 p-1">
                        <i className={`bi bi-check-circle-fill text-white ${config.checkBg} rounded-circle`} style={{ fontSize: '1.1rem' }}></i>
                    </div>
                )}
                <div className="card-body p-2 text-center">
                    <div className="mb-1">
                        <i className={`bi ${config.icon} ${isSelected ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}></i>
                    </div>
                    <h6 className={`card-title mb-1 ${isSelected ? 'text-dark fw-bold' : 'text-dark'}`} style={{ fontSize: '0.85rem' }}>
                        {config.number}
                    </h6>
                    <p className={`card-text mb-1 ${isSelected ? 'text-dark' : 'text-muted'}`} style={{ fontSize: '0.75rem' }}>
                        <i className="bi bi-geo-alt me-1"></i>
                        {record.UnitNumber || 'N/A'}
                    </p>
                    <span className={`badge ${config.badgeBg}`} style={{ fontSize: '0.65rem', padding: '0.25em 0.5em' }}>
                        {config.label}
                    </span>
                </div>
            </div>
        </div>
    );
};

const VoiceCallCustomerDetails = React.forwardRef<VoiceCallCustomerDetailsRef, VoiceCallCustomerDetailsProps>((
    { isOpen, onClose, callSid, existingCustomerDetails, locationId },
    ref
) => {
    const LoginUserDetails = useSelector((state: any) => state.auth.users);
    const [customerType, setCustomerType] = React.useState<"new" | "existing">("new");
    const [tenantDetails, setTenantDetails] = React.useState({
        tenantId: "",
        firstName: "",
        lastName: "",
        countryId: "1",
        facilityId: "",
        dateNeeded: "",
        advSource: "",
        classification: "",
        inquiryDetails: "",
        reservationDetails: "",
        rentalDetails: "",
    });
    const [dateNeeded, setDateNeeded] = React.useState<Date | null>(null);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [showNoDataMessage, setShowNoDataMessage] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [apiError, setApiError] = React.useState<string>("");
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [isFetchingByPhone, setIsFetchingByPhone] = React.useState(false);
    const [phoneSearchPerformed, setPhoneSearchPerformed] = React.useState(false);
    const [fetchedCustomerByPhone, setFetchedCustomerByPhone] = React.useState<any>(null);
    const [showPhoneNotFoundMessage, setShowPhoneNotFoundMessage] = React.useState(false);

    // Initialize when modal opens
    React.useEffect(() => {
        if (isOpen) {
            const defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 10);
            setDateNeeded(defaultDate);

            // For voice calls with existing customer data
            if (existingCustomerDetails && existingCustomerDetails.length > 0) {
                setCustomerType("existing");
                const customer = existingCustomerDetails[0];

                // Check if customer has any data
                const hasInquiries = customer.Inquiries && customer.Inquiries.length > 0;
                const hasReservations = customer.Reservations && customer.Reservations.length > 0;
                const hasRentals = customer.Rentals && customer.Rentals.length > 0;
                setShowNoDataMessage(!hasInquiries && !hasReservations && !hasRentals);

                setTenantDetails({
                    tenantId: customer.TenantID?.toString() || "",
                    firstName: customer.FirstName || "",
                    lastName: customer.LastName || "",
                    countryId: customer.CountryId?.toString() || "1",
                    facilityId: "",
                    dateNeeded: defaultDate.toISOString().split("T")[0],
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
            } else {
                // For voice calls without existing data
                setCustomerType("new");
                setTenantDetails({
                    tenantId: "",
                    firstName: "",
                    lastName: "",
                    countryId: "1",
                    facilityId: "",
                    dateNeeded: defaultDate.toISOString().split("T")[0],
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
            }
            // Reset states
            setIsSubmitted(false);
            setIsSubmitting(false);
            setApiError("");
            setErrors({});
            setPhoneNumber("");
            setIsFetchingByPhone(false);
            setPhoneSearchPerformed(false);
            setFetchedCustomerByPhone(null);
            setShowPhoneNotFoundMessage(false);
        }
    }, [isOpen, existingCustomerDetails]);

    const handleDateChange = (date: Date | null) => {
        setDateNeeded(date);
        setTenantDetails({
            ...tenantDetails,
            dateNeeded: date ? date.toISOString().split("T")[0] : ""
        });
    };

    const handlePhoneNumberChange = (value: string) => {
        setPhoneNumber(value);
        // Clear error when user starts typing
        if (errors.phoneNumber) {
            setErrors({ ...errors, phoneNumber: "" });
        }
        // Clear "not found" message when user modifies the number
        if (showPhoneNotFoundMessage) {
            setShowPhoneNotFoundMessage(false);
        }
    };

    const handleFetchCustomerByPhone = async () => {
        // Validate phone number
        const validation = validatePhoneNumber(phoneNumber);
        if (!validation.isValid) {
            setErrors({ ...errors, phoneNumber: validation.error });
            return;
        }

        setErrors({});
        setIsFetchingByPhone(true);
        setShowPhoneNotFoundMessage(false);
        try {
            const fetchPayload = {
                phoneNumber: removeCountryCode(phoneNumber.trim()),
                locationId: locationId,
                action: "fetch"
            };
            const res: any = await PostApiMethod("tenant/sendCallSummary/fetchCustomerDetailsByPhone", fetchPayload);
            console.log("Fetch by Phone API Response:", res);

            const apiData = res?.data?.data || [];

            if (apiData.length > 0 && apiData[0]) {
                const customer = apiData[0];
                // Store fetched customer data
                setFetchedCustomerByPhone(customer);

                // Switch to existing customer mode and populate data
                setCustomerType("existing");
                setPhoneSearchPerformed(true); // Only set this when customer is found

                // Check if customer has any data
                const hasInquiries = customer.Inquiries && customer.Inquiries.length > 0;
                const hasReservations = customer.Reservations && customer.Reservations.length > 0;
                const hasRentals = customer.Rentals && customer.Rentals.length > 0;
                setShowNoDataMessage(!hasInquiries && !hasReservations && !hasRentals);

                setTenantDetails({
                    tenantId: customer.TenantID?.toString() || "",
                    firstName: customer.FirstName || "",
                    lastName: customer.LastName || "",
                    countryId: customer.CountryId?.toString() || "1",
                    facilityId: "",
                    dateNeeded: dateNeeded ? dateNeeded.toISOString().split("T")[0] : "",
                    advSource: "",
                    classification: "",
                    inquiryDetails: "",
                    reservationDetails: "",
                    rentalDetails: "",
                });
                setApiError("");
            } else {
                // No customer found - keep search field visible
                setFetchedCustomerByPhone(null);
                setShowPhoneNotFoundMessage(true);
                setApiError("");
                // Don't set phoneSearchPerformed so user can search again
            }
        } catch (err) {
            console.error("Fetch by Phone API Error:", err);
            setApiError("Failed to fetch customer details. Please try again.");
            setFetchedCustomerByPhone(null);
            setShowPhoneNotFoundMessage(false);
        } finally {
            setIsFetchingByPhone(false);
        }
    };

    const handlePrimaryButtonClick = () => {
        const formElement = document.getElementById("voice-customer-details-form") as HTMLFormElement;
        if (formElement) {
            formElement.requestSubmit();
        }
    };

    const handleTenantDetailsSubmit = async (e?: React.FormEvent, isAutoSubmit: boolean = false) => {
        if (e) e.preventDefault();

        // Prevent duplicate submissions
        if (isSubmitted || isSubmitting) return;

        // Clear previous errors
        setErrors({});
        setApiError("");

        // Get facilityId from existingCustomerDetails or fetchedCustomerByPhone
        const getfacilityId = () => {
            if (existingCustomerDetails?.[0]?.facilityId) {
                return existingCustomerDetails[0].facilityId;
            }
            if (fetchedCustomerByPhone?.facilityId) {
                return fetchedCustomerByPhone.facilityId;
            }
            return null;
        };

        if (customerType === "new") {
            // Validate new customer
            const validationErrors = validateNewCustomer(tenantDetails, dateNeeded, isAutoSubmit);
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            // Get customer names from form data
            const customerFirstName = tenantDetails.firstName.trim();
            const customerLastName = tenantDetails.lastName.trim();

            // Build new customer payload
            const newCustomerPayload = buildNewCustomerPayload(
                tenantDetails,
                dateNeeded,
                callSid,
                locationId,
                getfacilityId(),
                LoginUserDetails?.name || "",
                customerFirstName,
                customerLastName
            );

            // Submit new customer
            setIsSubmitting(true);
            const result = await submitCustomerDetails(newCustomerPayload);
            setIsSubmitting(false);

            if (result.success) {
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose();
            } else {
                setApiError(result.error || "Failed to submit customer details");
            }
        } else {
            // Existing customer - Voice call (from voice call data or phone search)
            const customer = existingCustomerDetails?.[0] || fetchedCustomerByPhone;

            // Determine classification defaults using business logic
            const classificationDefaults = determineClassificationDefaults(
                tenantDetails.classification,
                customer
            );

            // Get customer names from form or fetched data
            const customerFirstName = tenantDetails.firstName.trim() || customer?.FirstName || "";
            const customerLastName = tenantDetails.lastName.trim() || customer?.LastName || "";

            // Build existing customer payload
            const existingCustomerPayload = buildExistingCustomerPayload(
                tenantDetails,
                customer,
                callSid,
                locationId,
                getfacilityId(),
                classificationDefaults,
                LoginUserDetails?.name || "",
                customerFirstName,
                customerLastName
            );

            // Submit existing customer
            setIsSubmitting(true);
            const result = await submitCustomerDetails(existingCustomerPayload);
            setIsSubmitting(false);

            if (result.success) {
                setIsSubmitted(true);
                if (!isAutoSubmit) onClose();
            } else {
                setApiError(result.error || "Failed to submit customer details");
            }
        }
    };

    // Expose autoSubmit function via ref
    React.useImperativeHandle(ref, () => ({
        autoSubmit: async () => {
            if (!isSubmitted) {
                await handleTenantDetailsSubmit(undefined, true);
            }
        }
    }));

    if (!isOpen) return null;

    return (
        <AppModal
            title="Customer Details"
            bodyText=""
            primaryButtonLabel={isSubmitting ? "Submitting..." : "Submit"}
            secondaryButtonLabel="Cancel"
            primaryButtonEventEmitter={handlePrimaryButtonClick}
            secondaryButtonEventEmitter={onClose}
            stayAppear={true}
        >
            <form id="voice-customer-details-form" onSubmit={handleTenantDetailsSubmit} style={{ position: 'relative' }}>
                {/* API Error Message */}
                {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Error:</strong> {apiError}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setApiError("")}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {/* Elegant Loading Overlay - For Submission */}
                {isSubmitting && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 1050,
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0.5rem'
                        }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem', borderWidth: '0.3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-3 text-white fw-medium">Processing...</div>
                        </div>
                    </div>
                )}

                {/* Loading Overlay - For Phone Fetch */}
                {isFetchingByPhone && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            zIndex: 1050,
                            backdropFilter: 'blur(4px)',
                            borderRadius: '0.5rem'
                        }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem', borderWidth: '0.3rem' }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-3 text-white fw-medium">
                                <i className="bi bi-telephone-fill me-2"></i>
                                Fetching Customer Details...
                            </div>
                        </div>
                    </div>
                )}

                {/* Voice call existing customer info (from voice call or phone search) */}
                {customerType === "existing" && (existingCustomerDetails?.[0] || fetchedCustomerByPhone) && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="alert alert-success border-success mb-0 py-2" style={{ borderLeft: '3px solid #198754' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-person-check-fill text-success me-2" style={{ fontSize: '1.25rem' }}></i>
                                    <div>
                                        <small className="mb-0 fw-bold text-success d-block">
                                            Existing Customer {fetchedCustomerByPhone && "(Found by Phone)"}
                                        </small>
                                        <small className="mb-0 text-dark">
                                            <strong>
                                                {existingCustomerDetails?.[0]?.FirstName || fetchedCustomerByPhone?.FirstName} {existingCustomerDetails?.[0]?.LastName || fetchedCustomerByPhone?.LastName}
                                            </strong>
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* New customer indicator with phone search */}
                {(!existingCustomerDetails || existingCustomerDetails.length === 0) && customerType === "new" && (
                    <>
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="alert alert-primary border-primary mb-0 py-2" style={{ borderLeft: '3px solid #0d6efd' }}>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-person-plus-fill text-primary me-2" style={{ fontSize: '1.25rem' }}></i>
                                        <div>
                                            <small className="mb-0 fw-bold text-primary d-block">New Customer</small>
                                            <small className="mb-0 text-muted">Search by phone number or fill in the details below</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Phone Number Search Field */}
                        {customerType === "new" && !phoneSearchPerformed && (
                            <div className="row mb-3">
                                <div className="col-9">
                                    <label className="form-label fw-semibold bg-light px-2 py-1 rounded">
                                        <i className="bi bi-telephone text-primary me-1"></i>
                                        Search by Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                        value={phoneNumber}
                                        onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                        placeholder="Enter phone number Ex: 9034675791"
                                        disabled={isFetchingByPhone}
                                    />
                                    {errors.phoneNumber && <small className="text-danger d-block mt-1">{errors.phoneNumber}</small>}
                                </div>
                                <div className="col-3 text-center" style={{ paddingTop: "40px" }}>
                                    <button
                                        type="button"
                                        className="btn btn-secondary w-100"
                                        onClick={handleFetchCustomerByPhone}
                                        disabled={isFetchingByPhone}
                                    >
                                        {isFetchingByPhone ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                Fetching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-search me-1"></i>
                                                Fetch
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Phone Not Found Message */}
                        {showPhoneNotFoundMessage && customerType === "new" && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="alert alert-warning d-flex align-items-center py-2 mb-0" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.2rem' }}></i>
                                        <div>
                                            <small className="mb-0">
                                                <strong>Customer not found.</strong> Please try a different number or fill the details below to proceed as a new customer.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* OR Divider */}
                        {customerType === "new" && !phoneSearchPerformed && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="d-flex align-items-center">
                                        <hr className="flex-grow-1" style={{ borderTop: '2px solid #dee2e6' }} />
                                        <span className="px-3 text-muted fw-bold" style={{ fontSize: '0.9rem' }}>OR</span>
                                        <hr className="flex-grow-1" style={{ borderTop: '2px solid #dee2e6' }} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* New Customer Form Section Label */}
                        {customerType === "new" && !phoneSearchPerformed && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="bg-light border rounded py-2 px-3">
                                        <small className="text-muted fw-semibold">
                                            <i className="bi bi-file-text me-2"></i>
                                            Enter Customer Information
                                        </small>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* No data message for existing customers */}
                {customerType === "existing" && showNoDataMessage && (
                    <div className="row mb-4 justify-content-center">
                        <div className="col-lg-10">
                            <div className="card border-warning shadow-lg" style={{ borderWidth: '3px' }}>
                                <div className="card-body text-center p-4">
                                    <div className="mb-3">
                                        <div className="position-relative d-inline-block">
                                            <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: '4rem' }}></i>
                                            <div className="position-absolute top-0 start-100 translate-middle">
                                                <span className="badge rounded-pill bg-warning">
                                                    <i className="bi bi-info-lg"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <h5 className="card-title text-warning fw-bold mb-3">No Customer Data Found</h5>
                                    <div className="alert alert-light border mb-4">
                                        <p className="card-text text-muted mb-2">
                                            This customer doesn't have any inquiry, reservation, or rental records.
                                        </p>
                                        <small className="text-muted">
                                            <i className="bi bi-arrow-right-circle me-1"></i>
                                            You can proceed with the new customer process using their existing details.
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-lg px-4 shadow"
                                        style={{ transition: 'all 0.2s ease-in-out' }}
                                        onClick={() => {
                                            setCustomerType("new");
                                            setShowNoDataMessage(false);
                                            setPhoneSearchPerformed(false);
                                            setFetchedCustomerByPhone(null);
                                            // Pre-populate TenantID from existing customer details or phone search
                                            const customer = existingCustomerDetails?.[0] || fetchedCustomerByPhone;
                                            if (customer?.TenantID) {
                                                setTenantDetails({
                                                    ...tenantDetails,
                                                    tenantId: customer.TenantID.toString()
                                                });
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <i className="bi bi-person-plus-fill me-2"></i>
                                        Continue as New Customer
                                    </button>
                                    <div className="mt-3">
                                        <div className="badge bg-light text-dark border">
                                            <i className="bi bi-check-circle-fill text-success me-1"></i>
                                            Tenant ID will be auto-filled
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Show all fields for new customers */}
                {customerType === "new" && (
                    <div className="mb-3">
                        {/* Tenant ID & Country ID */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="tenantId"
                                        id="tenantId"
                                        className={`form-control ${errors.tenantId ? 'is-invalid' : ''}`}
                                        value={tenantDetails.tenantId}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, tenantId: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Tenant ID"
                                    />
                                    <label htmlFor="tenantId">
                                        <i className="bi bi-person-badge text-primary me-2"></i>
                                        Tenant ID
                                    </label>
                                    {errors.tenantId && <div className="invalid-feedback">{errors.tenantId}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="countryId"
                                        id="countryId"
                                        className="form-control"
                                        value={tenantDetails.countryId}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, countryId: e.target.value })}
                                        placeholder="Country ID"
                                        disabled={isSubmitting}
                                    />
                                    <label htmlFor="countryId">
                                        <i className="bi bi-globe text-primary me-2"></i>
                                        Country Code
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* First & Last Name */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="firstName"
                                        id="firstName"
                                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.firstName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, firstName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="First Name"
                                    />
                                    <label htmlFor="firstName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        First Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        name="lastName"
                                        id="lastName"
                                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                        value={tenantDetails.lastName}
                                        onChange={(e) => setTenantDetails({ ...tenantDetails, lastName: e.target.value })}
                                        disabled={isSubmitting}
                                        placeholder="Last Name"
                                    />
                                    <label htmlFor="lastName">
                                        <i className="bi bi-person text-primary me-2"></i>
                                        Last Name <span className="text-danger">*</span>
                                    </label>
                                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Date Needed & Advertising Source */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="dateNeeded" className="form-label text-muted small mb-2">
                                    <i className="bi bi-calendar-event text-primary me-1"></i>
                                    Date Needed <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    id="dateNeeded"
                                    selected={dateNeeded}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className={`form-control ${errors.dateNeeded ? 'is-invalid' : ''}`}
                                    placeholderText="Select date needed"
                                    minDate={new Date()}
                                    disabled={isSubmitting}
                                />
                                {errors.dateNeeded && <div className="invalid-feedback d-block">{errors.dateNeeded}</div>}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="advSource" className="form-label text-muted small mb-2">
                                    <i className="bi bi-megaphone text-primary me-1"></i>
                                    Advertising Source <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.advSource ? 'is-invalid' : ''}`}
                                    id="advSource"
                                    name="advSource"
                                    value={tenantDetails.advSource}
                                    onChange={(e) => setTenantDetails({ ...tenantDetails, advSource: e.target.value })}
                                    disabled={isSubmitting}
                                >
                                    <option value="">Select...</option>
                                    <option value="Phone">Phone</option>
                                    <option value="Website">Website</option>
                                    <option value="Kiosk">Kiosk</option>
                                    <option value="QR code">QR Code</option>
                                </select>
                                {errors.advSource && <div className="invalid-feedback">{errors.advSource}</div>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Call Purpose - only for existing customers */}
                {customerType === "existing" && !showNoDataMessage && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-2 d-inline-flex align-items-center">
                                <i className="bi bi-telephone text-primary me-2"></i>
                                Call Purpose
                            </label>
                            <select
                                className="form-select form-select-lg"
                                style={{
                                    borderWidth: '2px',
                                    fontSize: '1rem',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                value={tenantDetails.classification}
                                onChange={(e) => setTenantDetails({ ...tenantDetails, classification: e.target.value })}
                                disabled={isSubmitting}
                            >
                                <option value="">-- Select Call Purpose --</option>
                                <option value="Inquiry">📋 Inquiry</option>
                                <option value="Reservation">📅 Reservation</option>
                                <option value="Rental">🏠 Rental</option>
                            </select>
                            {!tenantDetails.classification && (
                                <small className="text-muted mt-1 d-block">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Please select the purpose of this call
                                </small>
                            )}
                        </div>
                    </div>
                )}

                {/* Inquiry Details - Square Cards */}
                {customerType === "existing" && !showNoDataMessage && tenantDetails.classification === "Inquiry" && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-question-circle text-info me-2"></i>
                                Select Inquiry
                            </label>
                            {((existingCustomerDetails?.[0] || fetchedCustomerByPhone)?.Inquiries?.length > 0) ? (
                                <div className="row g-3">
                                    {(existingCustomerDetails?.[0] || fetchedCustomerByPhone)?.Inquiries.map((inquiry: any, index: number) => (
                                        <RecordCard
                                            key={index}
                                            record={inquiry}
                                            type="inquiry"
                                            isSelected={tenantDetails.inquiryDetails === inquiry.InquiryNumber}
                                            onSelect={() => setTenantDetails({
                                                ...tenantDetails,
                                                inquiryDetails: tenantDetails.inquiryDetails === inquiry.InquiryNumber ? "" : inquiry.InquiryNumber
                                            })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState type="inquiry" />
                            )}
                        </div>
                    </div>
                )}

                {/* Reservation Details - Square Cards */}
                {customerType === "existing" && !showNoDataMessage && tenantDetails.classification === "Reservation" && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-calendar-check text-warning me-2"></i>
                                Select Reservation
                            </label>
                            {((existingCustomerDetails?.[0] || fetchedCustomerByPhone)?.Reservations?.length > 0) ? (
                                <div className="row g-3">
                                    {(existingCustomerDetails?.[0] || fetchedCustomerByPhone)?.Reservations.map((reservation: any, index: number) => (
                                        <RecordCard
                                            key={index}
                                            record={reservation}
                                            type="reservation"
                                            isSelected={tenantDetails.reservationDetails === reservation.ReservationNumber}
                                            onSelect={() => setTenantDetails({
                                                ...tenantDetails,
                                                reservationDetails: tenantDetails.reservationDetails === reservation.ReservationNumber ? "" : reservation.ReservationNumber
                                            })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState type="reservation" />
                            )}
                        </div>
                    </div>
                )}

                {/* Rental Details - Square Cards */}
                {customerType === "existing" && !showNoDataMessage && tenantDetails.classification === "Rental" && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-house text-success me-2"></i>
                                Select Rental
                            </label>
                            {((existingCustomerDetails?.[0] || fetchedCustomerByPhone)?.Rentals?.length > 0) ? (
                                <div className="row g-3">
                                    {(existingCustomerDetails?.[0] || fetchedCustomerByPhone)?.Rentals.map((rental: any, index: number) => (
                                        <RecordCard
                                            key={index}
                                            record={rental}
                                            type="rental"
                                            isSelected={tenantDetails.rentalDetails === rental.LeaseNumber}
                                            onSelect={() => setTenantDetails({
                                                ...tenantDetails,
                                                rentalDetails: tenantDetails.rentalDetails === rental.LeaseNumber ? "" : rental.LeaseNumber
                                            })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState type="rental" />
                            )}
                        </div>
                    </div>
                )}
            </form>
        </AppModal>
    );
});

VoiceCallCustomerDetails.displayName = 'VoiceCallCustomerDetails';

export default VoiceCallCustomerDetails;